import cn from 'classnames';
import emoji from 'emoji-dictionary';
import type {
  GetStaticPaths,
  GetStaticProps,
  InferGetStaticPropsType,
} from 'next';
import type { ParsedUrlQuery } from 'querystring';
import React, { useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import htmlParser from 'react-markdown/plugins/html-parser';

import markdownStyle from '../../css/markdown.module.css';
import useGa from '../../hooks/useGa';
import ROUTES from '../../routes';

import styles from '@components/blog/Post/post.module.css';
import Button from '@components/Button';
import CaseStudyHero from '@components/CaseStudyHero';
import CodeBlock from '@components/CodeBlock';
import Container from '@components/Container';
import CTA from '@components/CTA';
import CustomerLogos from '@components/CustomerLogos';
import CustomHead from '@components/CustomHead';
import Quote from '@components/features/Quote';
import Footer from '@components/FooterNew';
import Header from '@components/HeaderNew';
import Heading from '@components/Heading';
import HeadingRenderer from '@components/HeadingRenderer';
import ImageRenderer from '@components/ImageRenderer';
import { Context } from '@contexts/AppContext';
import { getPublishedCaseStudies } from '@utils/airtableClient';
import { revalidate } from '@utils/constants';
import { replaceAttachments } from '@utils/s3';
import { slugify } from '@utils/slugify';

interface Params extends ParsedUrlQuery {
  customer: string;
}

type CaseStudy = Awaited<ReturnType<typeof getPublishedCaseStudies>>[number];

export const getStaticPaths: GetStaticPaths<Params> = async () => {
  const caseStudies = await getPublishedCaseStudies();
  return {
    fallback: false,
    paths: caseStudies.map((caseStudy) => ({
      params: {
        customer: slugify(caseStudy.customer_name),
      },
    })),
  };
};

export const getStaticProps: GetStaticProps<
  { record: CaseStudy },
  Params
> = async (ctx) => {
  const record = (await getPublishedCaseStudies()).find(
    (cs) => slugify(cs.customer_name) === ctx.params.customer,
  );
  await replaceAttachments('case_studies', () => record.customer_name, record);
  try {
    return {
      props: {
        record,
      },
      revalidate,
    };
  } catch (error) {
    console.log(error);
    return {
      props: {
        err: 'Something went wrong 😕',
        record: null,
      },
    };
  }
};

const parseHtml = htmlParser({
  isValidNode: (node) => node.type !== 'script',
});
const emojiSupport = (text) =>
  text.value.replace(/:\w+:/gi, (name) => emoji.getUnicode(name));

const renderers = {
  code: CodeBlock,
  heading: (props) => <HeadingRenderer {...props} />,
  image: ImageRenderer,
  text: emojiSupport,
};

const Metric = ({ number, insight }) => {
  return (
    <li className="font-inter font-bold text-center w-full lg:w-1/3 my-16 px-8 md:px-20">
      <div className="text-7xl tracking-tightest">{number}</div>
      <div className="text-lg">{insight}</div>
    </li>
  );
};

const Person = ({ name, title, image }) => {
  return (
    <li className="font-inter font-bold text-center w-full lg:w-1/3 my-16 px-8 md:px-20">
      <img src={image} className="w-20 h-20 rounded-full mb-4 inline-block" />
      <div className="text-lg">{name}</div>
      <div className="text-md opacity-50">{title}</div>
    </li>
  );
};

const CaseStudyPage = ({
  record,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const customerSlug = slugify(record.customer_name);
  const { isLoggedIn } = useContext(Context);
  const gaClickDemo = useGa('Request demo', 'Hero Features');

  return (
    <>
      <CustomHead
        meta={{
          title: `${record.customer_name} | Case Studies | PopSQL`,
          description: `${record.headline}`,
          image:
            record.og_image_url ||
            record.og_image[0].url ||
            `${process.env.NEXT_PUBLIC_ASSET_HOST}/static/images/og/default.png`,
          url: `${process.env.NEXT_PUBLIC_WEB_HOST}/case-studies/${customerSlug}`,
        }}
        openGraph={undefined}
      />
      <main>
        <Header />
        {/* @ts-expect-error fixme once CaseStudyHero is typed */}
        <CaseStudyHero
          title={`${record.headline}`}
          logo={record.customer_logo && `${record.customer_logo[0].url}`}
          image={record.cover_image && `${record.cover_image[0].url}`}
        />

        <Container
          singleColumn
          className={'flex fade fade-1 pb-0 max-w-screen-xl w-full'}
        >
          <Quote
            // @ts-expect-error fixme once Quote is typed
            title={false}
            quote={`${record.quote_body}`}
            quoteSize="text-2xl lg:text-3xl"
            // @ts-expect-error fixme once Quote is typed
            author={{
              name: `${record.quote_author_name}`,
              position: `${record.quote_author_title}`,
              company: `${record.customer_name}`,
            }}
            rawLogo
            logo={`${record.customer_logo[0].url}`}
          />

          <div className="max-w-3xl w-full px-6 pt-8 pb-16 mx-auto">
            <Heading Tag="h2" size="md" className="mt-6 mb-6">
              {`About ${record.customer_name}`}
            </Heading>
            <ReactMarkdown
              className={cn(
                markdownStyle.popsql__markdown,
                styles.postMarkdown,
              )}
              source={record.about}
              escapeHtml={false}
              renderers={renderers}
              astPlugins={[parseHtml]}
            />
          </div>

          <div
            style={{
              background: record.customer_color || '#073A75',
            }}
            className="rounded-lg px-4 md:px-16 mb-6"
          >
            <ul className="justify-center text-white rounded-md lg:flex items-center">
              {record.metric_number_1 && (
                <Metric
                  number={`${record.metric_number_1}`}
                  insight={`${record.metric_description_1}`}
                />
              )}

              {record.metric_number_2 && (
                <Metric
                  number={`${record.metric_number_2}`}
                  insight={`${record.metric_description_2}`}
                />
              )}

              {record.metric_number_3 && (
                <Metric
                  number={`${record.metric_number_3}`}
                  insight={`${record.metric_description_3}`}
                />
              )}
            </ul>
            <ul className="justify-center text-white lg:flex items-center">
              {record.person_name_1 && (
                <Person
                  image={`${record.person_picture_1[0].url}`}
                  name={`${record.person_name_1}`}
                  title={`${record.person_title_1}`}
                />
              )}

              {record.person_name_2 && (
                <Person
                  image={`${record.person_picture_2[0].url}`}
                  name={`${record.person_name_2}`}
                  title={`${record.person_title_2}`}
                />
              )}

              {record.person_name_3 && (
                <Person
                  image={`${record.person_picture_3[0].url}`}
                  name={`${record.person_name_3}`}
                  title={`${record.person_title_3}`}
                />
              )}
            </ul>
          </div>

          <div className="max-w-3xl w-full px-6 pt-8 pb-16 mx-auto">
            <ReactMarkdown
              className={cn(
                markdownStyle.popsql__markdown,
                styles.postMarkdown,
              )}
              source={record.body}
              escapeHtml={false}
              renderers={renderers}
              astPlugins={[parseHtml]}
            />
          </div>
        </Container>

        <CustomerLogos />

        <div className="fade fade-3 px-4">
          {isLoggedIn ? (
            <div className="w-full max-w-screen-xl mx-auto bg-white shadow-cta rounded-xl border border-gray-100 py-8 md:py-24 mb-20">
              <div className={`z-10 w-full p-4 text-center`}>
                <Heading Tag="h2" size="lg" customStyle={{ fontWeight: '700' }}>
                  Join thousands of teams writing SQL together
                </Heading>

                <Button
                  href={ROUTES.demo}
                  onClick={gaClickDemo}
                  variant="primary"
                  className="mt-6 md:mt-12 mx-auto max-w-[180px]"
                >
                  Request demo
                </Button>
              </div>
            </div>
          ) : (
            <CTA
              className="lg:mb-12"
              title="Join thousands of teams writing SQL together"
              description=""
              cta={{
                text: 'Get started',
                href: `${process.env.NEXT_PUBLIC_WEB_HOST}/users/sign_up`,
                onClick: false,
              }}
              withImage={false}
              withContactUs
            />
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default CaseStudyPage;
